import { FormStore, GlobalStore, UserService } from '@roc/feature-app-core';
import { isNil } from '@roc/feature-utils';
import { formatPercentage } from '@roc/ui/utils';
import { action, computed, makeObservable, observable } from 'mobx';
import { sum } from '../../utils/utils';
import { FeeFormStore } from '../common/feeFormStore';
import { LeverageFormStore } from '../fixFlip/leverageFormStore';
import { EMPTY_FEE_NAME_MESSAGE, FeeNames, InterestAccrualMethod, LeveragePricingType } from '../../utils/constants';
import { ILoanEconomicsStore, IQuoteTypeStore } from '../common/iQuoteTypeStore';
import { StabilizedBridgeLeverageFormStore } from './stabilizedBridgeLeverageFormStore';
import { StabilizedBridgeStore } from '../stabilizedBridgeStore';

const loanEconomicsForm = {
  fields: {
    interestRate: {
      value: 12,
      error: null,
      rule: '',
    },
    loanTermMonths: {
      value: 12,
      error: null,
      rule: '',
    },
    hasBroker: {
      value: null,
      error: null,
      rule: 'required',
      message: 'This field is required',
    },
    brokerPoints: {
      value: 0,
      error: null,
      rule: '',
    },
    interestAccrualMethod: {
      value: InterestAccrualMethod.INTEREST_ONLY,
      error: null,
      rule: 'required',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class StabilizedBridgeLoanEconomicsStore implements ILoanEconomicsStore {
  protected globalStore: GlobalStore;
  public rateAdjustmentsData: any;

  quoteTypeStore: StabilizedBridgeStore;

  interestReserveFeeStore: FeeFormStore;
  thirdPartyCostsFeeStore: FeeFormStore;
  originationPointsFeeStore: FeeFormStore;
  feeStores: FeeFormStore[];
  loanEconomicsFormStore: FormStore;

  leverageFormStore: StabilizedBridgeLeverageFormStore;

  interestReserveFinanced: boolean;
  showFinancedInterestReserve: boolean;

  instaConstructionHoldback: number;
  manualInitialLoanAmount: number;
  manualConstructionHoldback: number;

  constructor(globalStore: GlobalStore, quoteTypeStore: StabilizedBridgeStore) {
    this.globalStore = globalStore;
    this.quoteTypeStore = quoteTypeStore;

    this.loanEconomicsFormStore = new FormStore(loanEconomicsForm, globalStore);

    this.leverageFormStore = new StabilizedBridgeLeverageFormStore(globalStore);

    this.interestReserveFeeStore = new FeeFormStore(
      globalStore,
      this,
      FeeNames.INTEREST_RESERVE
    );
    this.thirdPartyCostsFeeStore = new FeeFormStore(
      globalStore,
      this,
      FeeNames.THIRD_PARTY_FEES
    );
    this.originationPointsFeeStore = new FeeFormStore(
      globalStore,
      this,
      FeeNames.ORIGINATION_POINTS,
      true
    );

    makeObservable(this, {
      reset: action,
      feeStores: observable,
      instaConstructionHoldback: observable,
      manualInitialLoanAmount: observable,
      manualConstructionHoldback: observable,
      rateAdjustmentsData: observable,
      calculateFields: action,
      calculateKeyRatios: action,
      loadLoanEconomics: action,
      getLoanEconomics: action,
      showFinancedInterestReserve: observable,
      interestReserveFinanced: observable,
      validateLeverageStep: action,
      validateLoanEconomicsStep: action,
    });
  }

  reset() {
    this.interestReserveFinanced = false;
    this.feeStores = [];
    this.rateAdjustmentsData = {};
    this.leverageFormStore.reset();
    this.instaConstructionHoldback = 0;
    this.manualInitialLoanAmount = 0;
    this.manualConstructionHoldback = 0;

    this.interestReserveFeeStore.reset();
    this.thirdPartyCostsFeeStore.reset();
    this.originationPointsFeeStore.reset();
  }

  loadLoanEconomics(quoteData) {
    const data = quoteData?.loanEconomics;
    if (!isNil(data)) {
      const fees = data.fees || [];
      this.feeStores = [];

      this.interestReserveFeeStore.loadForm({
        name: FeeNames.INTEREST_RESERVE,
        usePercentage: false,
        lenderAmount: data.amounts.interestReserve ?? 0,
      })
      this.thirdPartyCostsFeeStore.loadForm({
        name: FeeNames.THIRD_PARTY_FEES,
        usePercentage: false,
        lenderAmount: data.amounts.thirdParyCosts ?? 0,
      })
      this.originationPointsFeeStore.loadForm({
        name: FeeNames.ORIGINATION_POINTS,
        usePercentage: true,
        lenderPoints: data.amounts.originationPoints ?? 0,
      })
      fees.forEach(fee => {
        const form = new FeeFormStore(this.globalStore, this);
        form.loadForm(fee);
        this.feeStores.push(form);
      });

      this.leverageFormStore.loadForm({
        initialAdvance: data.amounts?.initialLoanAmount ?? 0,
        constructionHoldback: data.amounts?.constructionHoldback ?? 0,
        pricingType: data.leverage?.pricingType ?? LeveragePricingType.LTC,
      });
      this.loanEconomicsFormStore.loadForm({
        interestRate: data.rates?.interestRate ?? 12,
        loanTermMonths: data.terms?.loanTermMonths ?? 12,
        totalPoints: data.totalPoints ?? 2,
        hasBroker: quoteData.broker?.hasBroker ? true : false,
        brokerPoints: quoteData.broker?.brokerPoints ?? 0,
        interestAccrualMethod: data.interestAccrualMethod ?? InterestAccrualMethod.INTEREST_ONLY,
      });
    } else {
      this.reset();
    }
  }

  getLoanEconomics() {
    const calculatedFields = this.calculateFields();
    const loanEconomicsData = this.loanEconomicsFormStore.getFormValues();
    const fees = this.feeStores
      .filter(form => !form.isEmpty)
      .map(form => form.getFeeValues(calculatedFields.totalLoanAmount));

    return {
      totalPoints: loanEconomicsData.totalPoints,
      leverage: {
        loanToCost: calculatedFields.loanToCost,
        loanToValue: calculatedFields.loanToValue,
        pricingType: this.leverageFormStore.form?.fields?.pricingType?.value ?? LeveragePricingType.LTC,
      },
      amounts: {
        totalLoanAmount: calculatedFields.totalLoanAmount, // Calculated
        initialLoanAmount: calculatedFields.initialAdvance, // Calculated
        constructionHoldback: calculatedFields.constructionHoldback, // Calculated
        interestReserve: this.interestReserveFeeStore.form.fields.lenderAmount.value,
        thirdParyCosts: this.thirdPartyCostsFeeStore.form.fields.lenderAmount.value,
        originationPoints: this.originationPointsFeeStore.form.fields.lenderPoints.value,
      },
      rates: {
        interestRate: loanEconomicsData.interestRate,
      },
      terms: {
        loanTermMonths: loanEconomicsData.loanTermMonths,
      },
      fees: fees,
      interestAccrualMethod: loanEconomicsData.interestAccrualMethod,
    };
  }

  getBroker() {
    const { hasBroker, brokerPoints } = this.loanEconomicsFormStore.form.fields;
    return hasBroker.value
      ? {
        hasBroker: hasBroker.value,
        brokerPoints: brokerPoints.value,
      }
      : null;
  }

  getCalculatedData() {
    const calculatedFields = this.calculateFields();
    return {
      totalPurchasePrice: calculatedFields.totalPurchasePrice,
      totalRenovationBudget: calculatedFields.totalProposedConstructionBudget,
      totalAfterRepairValue: calculatedFields.totalAfterRepairValue,
      afterRepairLoanToValue: calculatedFields.afterRepairLTV,
      totalAsIsValue: calculatedFields.totalAsIsValue,
    };
  }

  addNewFee() {
    this.feeStores.push(new FeeFormStore(this.globalStore, this));
  }

  removeFee(feeKey: string) {
    this.feeStores = this.feeStores.filter(fee => fee.key !== feeKey);
  }

  get totalLoanAmount() {
    const calculatedFields = this.calculateFields();
    return calculatedFields.totalLoanAmount;
  }

  get interestAccrualMethodOptions() {
    return [
      { label: InterestAccrualMethod.INTEREST_ONLY, value: InterestAccrualMethod.INTEREST_ONLY },
    ];
  }

  calculateFields() {
    const brokerPoints =
      this.loanEconomicsFormStore.form.fields.brokerPoints.value || 0;
    const properties = this.quoteTypeStore.propertiesStore.getProperties();

    const initialAdvance = this.leverageFormStore.form.fields.initialAdvance.value;
    const constructionHoldback = this.leverageFormStore.form.fields
      .constructionHoldback.value;

    const totalConstructionRenoSpendToDate = this.quoteTypeStore
      .isMidConstructionRefinance
      ? sum(properties.map(p => p.renovation.capitalImprovements))
      : 0;

    const totalSellerCredit = sum(
      properties.filter(p => !!p.sellerCredit).map(p => p.sellerCreditAmount)
    );

    const totalPurchasePrice =
      sum(properties.map(p => p.financials.purchasePrice)) +
      totalConstructionRenoSpendToDate -
      totalSellerCredit;
    const totalProposedConstructionBudget = sum(
      properties.map(p => p.financials.renovationBudget)
    );
    const totalAfterRepairValue = sum(
      properties.map(p => p.financials.marketValueAfterCompletion)
    );
    const totalAsIsValue = sum(
      properties.map(p => p.financials.currentMarketValue)
    );
    const totalPayoffAmount = sum(
      properties.map(p => p.financials.payoffAmount)
    );

    const loanToCost = (initialAdvance / totalPurchasePrice) * 100;
    const loanToValue = (initialAdvance / totalAsIsValue) * 100;
    const constructionReserve = (constructionHoldback / totalProposedConstructionBudget) * 100;

    const totalLoanAmount = initialAdvance;
    const afterRepairLTV = (totalLoanAmount / totalAfterRepairValue) * 100;
    const ltfc =
      (totalLoanAmount /
        (totalPurchasePrice + totalProposedConstructionBudget)) *
      100;

    const brokerOriginationFees = (brokerPoints * totalLoanAmount) / 100;

    const interestReserve =
      this.interestReserveFeeStore.getFeeValues(totalLoanAmount).lenderAmount ??
      0;
    const thirdPartyCosts =
      this.thirdPartyCostsFeeStore.getFeeValues(totalLoanAmount).lenderAmount ??
      0;
    const originationFees =
      this.originationPointsFeeStore.getFeeValues(totalLoanAmount)
        .lenderAmount ?? 0;

    const lenderFees = this.feeStores.map(feeStore =>
      feeStore.getFeeValues(totalLoanAmount)
    );
    const totalLenderFees = sum(lenderFees.map(fee => fee.lenderAmount ?? 0)) + originationFees;

    return {
      loanToCost,
      loanToValue,
      totalAsIsValue,
      totalLoanAmount,

      constructionReserve,
      totalPurchasePrice,
      totalProposedConstructionBudget,
      totalAfterRepairValue,
      initialAdvance,
      constructionHoldback,
      afterRepairLTV,
      ltfc,
      brokerOriginationFees,
      lenderFees,
      totalLenderFees,
      interestReserve,
      thirdPartyCosts,
      originationFees,
      totalPayoffAmount,
    };
  }

  calculateKeyRatios() {
    const calculatedFields = this.calculateFields();
    return this.isLTC() ? `${formatPercentage(calculatedFields.loanToCost)}` : `${formatPercentage(calculatedFields.loanToValue)}`;
  }

  isLTC() {
    return this.leverageFormStore.form?.fields?.pricingType?.value === LeveragePricingType.LTC;
  }

  validateLeverageStep() {
    return true;
  }

  validateLoanEconomicsStep() {
    if (this.feeStores.some(f => f.isEmpty)) {
      this.globalStore.notificationStore.showErrorNotification({
        message: EMPTY_FEE_NAME_MESSAGE,
      });
      return false;
    }
    return true;
  }
}
